import React, { useState, useEffect } from 'react'
import { useSnackbar } from 'notistack';
import { Button, Grid, Paper, Skeleton, Table, TableBody, TableHead, TableRow, TableCell, TableSortLabel, ButtonGroup,Box } from '@mui/material';
import { getActivitiesListApi } from '../apiService';
import { getAssetListApi } from '../../leases/apiServices';
import { trackActivity } from '../../../utils/mixpanel';
import { NoRecordFound, PageLoader, Pagination, STableLoader, FilterComponent } from '../../../shared_elements';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import ActivitiesListing from '../components/ActivitiesListing';
import CustomDatePopover from '../components/CustomDatePopover';
import moment from 'moment';
import { getDateRange } from '../../../utils/helper';

const activityTypeOptions = [
    { label: 'Create', value: 'create' },
    { label: 'Update', value: 'update' },
    { label: 'Delete', value: 'delete' },
    { label: 'Export', value: 'export'}
];

const FleetActivitiesHd = ({ sortInfo, createSortHandler }) => {
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: ` Fleet Activities`, page_type: page_type, })
    }
    const tableSorting = (key, name) => {
        return (
            <TableSortLabel
                active={sortInfo.sort_by === key ? true : false}
                direction={sortInfo.sort_by === key ? sortInfo.sort : 'asc'}
                onClick={() => { createSortHandler(key); handleItemClicked(`Table Sorting: ${name}`) }}
            >
                {name}
            </TableSortLabel>
        )
    }
    return (
        <TableHead>
            <TableRow>
                <TableCell sx={{ minWidth: "270px" }}>Activity Info</TableCell>
                 <TableCell sx={{ minWidth: "110px" }}>{tableSorting('asset_name', 'Asset')}</TableCell>
                 <TableCell sx={{ minWidth: "160px" }}>{tableSorting('lease_reference_code', 'Lease Reference Code')}</TableCell>
                <TableCell sx={{ minWidth: "140px" }}>{tableSorting('module_name', 'Module Name')}</TableCell>
                <TableCell sx={{ minWidth: "110px" }}>{tableSorting('activity_type', 'Activity Type ')}</TableCell>
                <TableCell sx={{ minWidth: "220px" }}>{tableSorting('performed_by', 'Performed By')}    </TableCell>
                <TableCell sx={{ minWidth: "140px" }}>Performed At</TableCell>
                <TableCell sx={{ minWidth: "120px" }}>Device IP</TableCell>
                <TableCell sx={{ minWidth: "110px" }}>Device Type</TableCell>
                <TableCell sx={{ minWidth: "120px" }}>Browser Type</TableCell>
                <TableCell sx={{ minWidth: "110px" }}>OS Type</TableCell>
            </TableRow>
        </TableHead>
    )
}

const FleetActivities = () => {
    const { enqueueSnackbar } = useSnackbar()
    const [skeletonLoader, setSkeletonLoader] = useState(true);
    const [activitiesData, setActivitiesData] = useState({ mode: true })
    const [isLoading, setLoading] = useState(false);
    const [popoverData, setPopoverData] = useState({ modal: false, data: {} });
    const [customDateData, setCustomDateData] = useState({});
    const [dateFilterkey, setDateFilterKey] = useState("");
    const [error, setError] = useState({});
    const [filter, setFilter] = useState({});
    const [assetList, setAssetList] = useState([]);
    const [queryConfig, setQueryConfig] = useState({
        page: activitiesData?.pagination?.page || 1,
        per_page: activitiesData?.pagination?.per_page || 50,
    });
    const [isExport, setExport] = useState();

    const activitiesFilterMenu = {
        related_asset_id: { title: 'Asset', inputType: 'dropdown', options: assetList || [], valueKey: 'unique_key', labelKey: 'name', multiple: true,   renderOption: (props, option) => ( <Box component="li" {...props} key={option.slug || option.unique_key}> {option.name} </Box>) },
        reference_code: { title: 'Lease Reference Code', inputType: 'text' },
        module_display_name: { title: 'Module Name', inputType: 'text' },
        activity_type: { title: 'Activity Type', inputType: 'dropdown', options: activityTypeOptions, valueKey: 'value', labelKey: 'label', multiple: false },
        os_name: { title: 'OS Name', inputType: 'text' },
        browser_name: { title: 'Browser Name', inputType: 'text' },
        performed_by: { title: "Performed By", inputType: 'text'},
        performed_at_after: { title: 'Performed From', inputType: 'date' },
        performed_at_before: { title: 'Performed To', inputType: 'date' },
    };

    useEffect(() => {
        getActivitiesList({ per_page: 50,page: 1, }, 'skeletonLoader');
        trackActivity('Page Viewed', { page_title: 'Fleet Activities' });
        globalGetService(getAssetListApi()).then(response => {
            const formatted = (response?.data?.data || []).map(item => ({
                ...item,
                unique_key: `${item.id}_${item.asset_type}`,
            }));
            setAssetList(formatted);
        });
    }, [])

    const onToggleModal = () => { setCustomDateData({}); setError({}); setPopoverData({ modal: false, event: null }) }

    const getActivitiesList = (query = {}, loaderType) => {
        loaderType === 'skeletonLoader' ? setSkeletonLoader(true) : setLoading(true);
        const apiQuery = { ...query };
        if (apiQuery.related_asset_id) {
            apiQuery.related_asset_id = apiQuery.related_asset_id .split(',').map(key => { const [id, assetType] = key.split('_'); return `(${id},${assetType})`; });
        }
        globalGetService(getActivitiesListApi(apiQuery)).then(response => {
            if (checkApiStatus(response)) {
                setActivitiesData(response?.data?.data);
                loaderType === 'skeletonLoader' ? setSkeletonLoader(false) : setLoading(false);
                onToggleModal()
            }
        })
    }

    const onApplyFilter = (query) => {
        setFilter(query);
        getActivitiesList({ ...queryConfig, ...query, page: 1, per_page: activitiesData?.pagination?.per_page || 50 });
    };

    const onFieldChange = (key, value) => { setCustomDateData({ ...customDateData, [key]: value }) }
    const onFocusChange = (key) => { setError({ ...error, [key]: "" }) }
    const createSortHandler = (key) => {
        const isAsc = queryConfig.sort_by === key && queryConfig.sort === 'asc';
        const updatedSort = isAsc ? 'desc' : 'asc';
        setQueryConfig({
            ...queryConfig,
            page: activitiesData?.pagination?.page || 1,
            per_page: activitiesData?.pagination?.per_page || 50,
            sort_by: key,
            sort: updatedSort
        });
        getActivitiesList({
            ...filter,
            ...queryConfig,
            page: activitiesData?.pagination?.page || 1,
            per_page: activitiesData?.pagination?.per_page || 50,
            sort: updatedSort,
            sort_by: key,
        }, "");
    };
    const onCustomDateApply = (key) => {
        setDateFilterKey(key)
        if (key === "is_custom") {
            const startDate = moment(customDateData?.start_date);
            const endDate = moment(customDateData?.end_date);
            const validationInputs = {
                start_date: customDateData?.start_date && startDate.isValid() ? "" : "Please enter valid Start Date",
                end_date: customDateData?.end_date && endDate.isValid() ? "" : "Please enter valid End Date",
            };
            if (customDateData?.start_date && customDateData?.end_date && startDate.isValid() && endDate.isValid()) {
                validationInputs['end_date'] = endDate.isBefore(startDate) ? "End Date must be after Start Date" : "";
            }
            if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
                const query = { performed_at_before: customDateData?.end_date ? customDateData?.end_date : null, performed_at_after: customDateData.start_date ? customDateData.start_date : null }
                setQueryConfig({ ...queryConfig, ...query });
                getActivitiesList({ ...filter, ...queryConfig, ...query ,page: 1,})
            } else {
                setError(validationInputs);
            }
        } else {
            const query = { performed_at_after: getDateRange(key)?.start ? getDateRange(key)?.start : null, performed_at_before: getDateRange(key)?.end ? getDateRange(key)?.end : null }
            setQueryConfig({ ...queryConfig, ...query, });
            getActivitiesList({ ...filter, ...queryConfig, ...query,page: 1, })
        }
    }

    const handleExport = (query) => {
        setExport(true);
        globalGetService(getActivitiesList(query, "pageLoader")).then((response) => {
            if (checkApiStatus(response)) {
                enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } });
            }
            setExport(false);
        })
    }

    const handleClearDateFilter = () => {
        setDateFilterKey("")
        setQueryConfig({
            page: queryConfig?.pagination?.page || 1,
            per_page: queryConfig?.pagination?.per_page || 50,
        })
        getActivitiesList({
            ...filter,
            page: queryConfig?.pagination?.page || 1,
            per_page: queryConfig?.pagination?.per_page || 50,
        })
    };
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Fleet Activities`, page_type: page_type, })
    }

    return (
        <>
            <div className="active-asset-section">
                <Paper elevation={0} sx={{ padding: '5px', marginBottom: '2px' }} className="active-asset-header lease-module-paper">
                    <Grid container spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Grid size='grow' sx={{ minWidth: 0 }}>
                             {skeletonLoader ? <Skeleton variant="rounded" width={220} height={32} /> : <div><FilterComponent filter={filter} filterMenu={activitiesFilterMenu} getResponseBack={onApplyFilter} /></div>}
                        </Grid>
                        <Grid size={{ md: 'auto', xs: 12 }}>
                            {skeletonLoader ? <Skeleton variant="rounded" width={220} height={32} /> :
                            <ul className='list-inline'>
                                <li className="list-inline-item">
                                <ButtonGroup variant="outlined">
                                    <Button className='alert-btn-group' size="small" variant={dateFilterkey === "is_custom" ? "contained" : "outlined"} color="primary" onClick={(event) => { setPopoverData({ event: event.currentTarget, modal: true }); handleItemClicked('Clicked on Custom Date') }}>Custom Date</Button>
                                    <Button className='alert-btn-group' size="small" variant={dateFilterkey === "today" ? "contained" : "outlined"} color="primary" onClick={() => { onCustomDateApply("today"); handleItemClicked('Clicked on Today Button') }}>Today</Button>
                                    <Button className='alert-btn-group' size="small" variant={dateFilterkey === "yesterday" ? "contained" : "outlined"} color="primary" onClick={() => { onCustomDateApply("yesterday"); handleItemClicked('Clicked on Yesterday Button') }}>Yesterday</Button>
                                    <Button className='alert-btn-group' size="small" variant={dateFilterkey === "7days" ? "contained" : "outlined"} color="primary" onClick={() => { onCustomDateApply("7days"); handleItemClicked('Clicked on 7 Days Button') }}>7 Days</Button>
                                    <Button className='alert-btn-group' size="small" variant={dateFilterkey === "1month" ? "contained" : "outlined"} color="primary" onClick={() => { onCustomDateApply("1month"); handleItemClicked('Clicked on 1M Button') }} >1M</Button>
                                    {dateFilterkey && <Button
                                        size="small"
                                        color="error"
                                        variant={"outlined"}
                                        onClick={() => { handleClearDateFilter(); handleItemClicked('Clicked on Clear Button') }}
                                    >
                                        Clear
                                    </Button>}
                                </ButtonGroup>
                                </li>
                            </ul>}
                        </Grid>
                    </Grid>
                </Paper>
                <div className="active-asset-list">
                    {skeletonLoader ? <STableLoader count={6} rowCount={12} /> :
                        <Paper className="lease-module-paper" elevation={0} style={{ overflow: "auto", paddingBottom: '0px', boxSizing: 'border-box', ...(activitiesData?.list?.length > 0 ? { height: `${window.innerHeight - 175}px`, maxHeight: 'none' } : { height: 'auto', maxHeight: `${window.innerHeight - 175}px` }) }}>
                            <Table className="mui-table-format" stickyHeader>
                                <FleetActivitiesHd sortInfo={queryConfig} createSortHandler={createSortHandler} />
                                <TableBody>
                                    {activitiesData?.list?.length > 0 && activitiesData?.list?.map((item, index) => {
                                        return (
                                            <ActivitiesListing
                                                item={item}
                                                key={index}
                                                type='fleet'
                                            />
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </Paper>
                    }
                    {!skeletonLoader && activitiesData?.list?.length === 0 ? <NoRecordFound /> : null}
                    <Paper>
                        <Pagination
                            pagination={activitiesData?.pagination}
                            onPageChange={(event, newPage) => {
                                const pageNum = newPage + 1;
                                setQueryConfig({ ...queryConfig, page: pageNum, per_page: activitiesData?.pagination?.per_page });
                                getActivitiesList({ ...filter, ...queryConfig, page: pageNum, per_page: activitiesData?.pagination?.per_page });
                            }}
                            onRowsPerPageChange={(e) => {
                                const perPage = Number(e.target.value);
                                setQueryConfig({ ...queryConfig, page: 1, per_page: perPage });
                                getActivitiesList({ ...filter, ...queryConfig, page: 1, per_page: perPage });
                            }} />
                    </Paper>
                </div>
            </div>
            {popoverData?.modal ? <CustomDatePopover customDateData={customDateData} popoverData={popoverData} error={error} onFieldChange={onFieldChange} onFocusChange={onFocusChange} onCustomDateApply={onCustomDateApply} onToggleModal={onToggleModal} /> : null}
            {isLoading ? <PageLoader /> : null}
        </>
    )
}

export default FleetActivities;
